// ===== Production Error Boundary Component =====

import React from 'react';
import { Alert, Button, Typography, Box } from '@mui/material';
import { Error as ErrorIcon, Refresh } from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: Date.now() + Math.random()
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Report to error tracking service in production
    if (process.env.NODE_ENV === 'production' && window.errorReporting) {
      window.errorReporting.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        },
        tags: {
          section: 'chat'
        }
      });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          padding={3}
          textAlign="center"
        >
          <ErrorIcon 
            style={{ 
              fontSize: '64px', 
              color: '#ff4444', 
              marginBottom: '16px' 
            }} 
          />
          
          <Typography variant="h5" gutterBottom color="error">
            Something went wrong
          </Typography>
          
          <Typography variant="body1" color="textSecondary" paragraph>
            The chat system encountered an unexpected error. Please try refreshing or reloading the page.
          </Typography>

          <Box display="flex" gap={2} marginTop={2}>
            <Button 
              variant="outlined" 
              onClick={this.handleRetry}
              startIcon={<Refresh />}
            >
              Try Again
            </Button>
            
            <Button 
              variant="contained" 
              color="primary"
              onClick={this.handleReload}
            >
              Reload Page
            </Button>
          </Box>

          {isDevelopment && this.state.error && (
            <Alert 
              severity="error" 
              style={{ 
                marginTop: '24px', 
                textAlign: 'left',
                maxWidth: '100%',
                overflow: 'auto'
              }}
            >
              <Typography variant="h6" gutterBottom>
                Development Error Details:
              </Typography>
              
              <Typography variant="body2" component="pre" style={{ 
                whiteSpace: 'pre-wrap',
                fontSize: '12px',
                fontFamily: 'monospace'
              }}>
                <strong>Error:</strong> {this.state.error.message}
                {'\n\n'}
                <strong>Stack:</strong> {this.state.error.stack}
                {this.state.errorInfo && (
                  <>
                    {'\n\n'}
                    <strong>Component Stack:</strong> {this.state.errorInfo.componentStack}
                  </>
                )}
              </Typography>
            </Alert>
          )}

          <Typography 
            variant="caption" 
            color="textSecondary" 
            style={{ marginTop: '16px' }}
          >
            Error ID: {this.state.errorId}
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
